<script setup lang="ts">
import { useRoute } from 'vue-router';
import { computed, nextTick, onMounted, onUnmounted, Ref, ref, watch, inject, ComputedRef } from 'vue';
import { getLabel } from '@/utils/data-display-utils';
import { QLayout, QPage, QPageContainer, QPageSticky, QSkeleton } from 'quasar';
import { storeToRefs } from 'pinia';

import HomepageItemCard from '@/components/HomepageItemCard.vue';
import FormCard from '@/components/FormCard.vue';
import BaseCard from '@/components/base/BaseCard.vue';
import SectionContainer from '@/components/SectionContainer.vue';
import ContextMenu from '@/components/ContextMenu.vue';
import PropositionDialog from '@/components/dialogs/PropositionDialog.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import DemographicDialog from '@/components/dialogs/DemographicDialog.vue';
import HomepageItemPrivateNoAccessDialog from '@/components/dialogs/HomepageItemPrivateNoAccessDialog.vue';
import ConclusionMessage from '@/components/ConclusionMessage.vue';
import Banner from '@/components/layout/Banner.vue';

import { sectionsService } from '@/services/sections.service';
import { authService } from '@/services/auth.service';
import { formsService } from '@/services/forms.service';
import { contextService } from '@/services/context.service';
import { Consultation } from '@/store/consultation.interface';
import { Section } from '@/store/section.interface';
import { useCommunityStore } from '@/store/communityStore';
import { useUserStore } from '@/store/userStore';
import { getLanguageName, getTranslation } from '@/utils/data-display-utils';
import useResponsive from '@/utils/responsive-util';
import { constant } from '@/constant';
import { consultationsService } from '@/services/consultations.service';
import { useTracker } from '@/composables/useTracker';
import { getAltText } from '@/utils/image.helper';
import { StoredFile } from '@/store/common.interface';
import { currentLocale } from '@/plugins/i18n';

// REFS
const containerWidth = ref<string>('0px');
const activeSection = ref<Section | null>(null);
const stickyHeight = ref<string>('');
const propositionDialogRef = ref(null);
const demographicDialogRef = ref(null);
const homepageItemPrivateNoAccessDialogRef = ref(null);
const results = inject<Ref<boolean>>('results');
const userIsAllowed = ref({ allowed: false, reason: '' });

// CONST
const route = useRoute();
const { isMobile } = useResponsive();
const slug = route.params.slug as string;
const projectId = route.query.project_id as string | undefined;
const { track } = useTracker();

// STORE
const communityStore = useCommunityStore();
const { sectionLoaded, getSections, formsLoaded, community } = storeToRefs(communityStore);
const userStore = useUserStore();

// PROPS
const props = defineProps<{ isTemplate: boolean }>();

// LIFECYCLE
onMounted(async () => {
  if (props.isTemplate) {
    await consultationsService.getConsultationTemplateBySlug(slug);
  } else {
    await consultationsService.getConsultationBySlug(slug);
  }
  if (getConsultation.value) {
    userIsAllowed.value = communityStore.isUserAllowedAccessConsultation(getConsultation.value, userStore.user) as any;
    if (userIsAllowed.value.allowed || props.isTemplate) {
      await contextService.getConsultationContext(getConsultation.value.id);
      await sectionsService.getSections(getConsultation.value.id);
      activeSection.value = getSections.value(getConsultation.value.id)[0];
      setNextActiveTheme(activeSection.value.orderNum);
      document.title =
        getTranslation(community.value, 'title', getConsultation.value?.languageEnabled) +
        ' - ' +
        getTranslation(getConsultation.value, 'title', getConsultation.value?.languageEnabled);
    } else if (userIsAllowed.value.reason === 'NotAllowedByAdmin') {
      openHomepageItemPrivateNoAccessPopup();
    } else if (userIsAllowed.value.reason === 'login') {
      openHomepageItemPrivateNoAccessPopup();
    }
  } else {
    openHomepageItemPrivateNoAccessPopup();
  }
});

onUnmounted(() => {
  communityStore.sectionLoaded = false;
  communityStore.formsLoaded = false;
});

// WATCH
watch(sectionLoaded, (newValue, oldValue) => {
  if ('section' in route.query) {
    nextTick(() => {
      setActiveTheme(parseInt(route.query.section as string));
    });
  }
});
watch(formsLoaded, (newValue, oldValue) => {
  if ('form' in route.query) {
    nextTick(() => {
      scrollToQuestion(parseInt(route.query.form as string));
    });
  }
});

// COMPUTED
const getConsultation: ComputedRef<Consultation | null> = computed(() => {
  if (!communityStore.community.consultations) {
    return null;
  }
  return communityStore.community.consultations.find((consultation) => consultation.slug == slug);
});

const getBanner: ComputedRef<StoredFile | null> = computed(() => {
  if (getConsultation.value) {
    return getConsultation.value && getConsultation.value.bannerFile?.url
      ? getConsultation.value.bannerFile
      : communityStore.community.bannerFile;
  }
  return null;
});

const showAdminResults: ComputedRef<boolean> = computed(() => {
  return results.value;
});

const showQuestionResults: ComputedRef<boolean> = computed(() => {
  return (
    getConsultation.value?.status === constant.Consultation.Status.Completed ||
    getConsultation.value?.status === constant.Consultation.Status.Archived
  );
});

const sectionProhibitsResultsAndComments: ComputedRef<boolean> = computed(() => {
  return activeSection.value?.options?.hide_results_comments;
});

const activeSectionIndex: ComputedRef<number> = computed(() => {
  const sections = getSections.value(getConsultation.value.id);
  const list = sections.find((s) => s.id === activeSection.value.id);
  return sections.indexOf(list);
});

const project = computed(() => getConsultation.value.projects.find((project) => project.id === parseInt(projectId)));

// FUNCTIONS
async function setActiveTheme(id: number) {
  const sections = getSections.value(getConsultation.value.id);
  const section = sections.find((s) => s.id === id);
  activeSection.value = section;
  await contextService.getSectionContext(getConsultation.value.id, section.id);
  await formsService.getForms(section);
}

async function setNextActiveTheme(next: number) {
  const sections = getSections.value(getConsultation.value.id);
  const section = sections.find((s) => s.orderNum === next);
  activeSection.value = section;
  track({
    event: 'view_consultation_section',
    params: {
      consultation_id: getConsultation.value.id,
      section_id: section.id,
    },
  });
  await contextService.getSectionContext(getConsultation.value.id, section.id);
  await formsService.getForms(section);
}

function scrollToQuestion(index: number) {
  const nextForm = document.getElementById('fiche_' + index);
  if (nextForm != undefined) {
    nextForm.scrollIntoView({ block: 'center', behavior: 'smooth' });
  } else {
    const orderNum = activeSection.value.orderNum + 1;
    try {
      nextSection(orderNum);
    } catch {
      scrollSections();
    }
  }
}

async function nextSection(next) {
  const sections = getSections.value(getConsultation.value.id);
  // Find the section with the smallest orderNum that is >= next
  const nextSection = sections.filter((s) => s.orderNum >= next).sort((a, b) => a.orderNum - b.orderNum)[0];

  if (nextSection) {
    await setNextActiveTheme(nextSection.orderNum);
    await nextTick();
    scrollSections();
  }
}

function scrollNextQuestion(index: number) {
  const nextIndex = index + 1;
  scrollToQuestion(nextIndex);
}

function scrollSections() {
  if (isMobile) {
    window.scrollTo({ top: 20, behavior: 'smooth' });
  } else {
    window.scrollTo({ top: 80, behavior: 'smooth' });
  }
}

function openDialog() {
  if (!isUserLogged.value) {
    // console.log(getConsultation.value.slug, activeSection.value ? activeSection.value.id : null)
    authService.login({
      consultation: {
        consultationId: getConsultation.value.slug,
        form: activeSection.value ? activeSection.value.id : null,
      },
    });
  } else if (!userHasDemographicsData.value && community.value.options.use_demographics) {
    openDemographicPopup('proposal');
  } else {
    propositionDialogRef.value.openDialog();
  }
}

const isUserLogged = computed(() => {
  return userStore.isUserLogged;
});

const userHasDemographicsData = computed(() => {
  return userStore.validateDemographicData;
});

const isLastSection = computed(() => {
  const sections = getSections.value(getConsultation.value.id);
  const list = sections.find((s) => s.id === activeSection.value.id);
  const index = sections.indexOf(list);
  return index + 1 === getSections.value(getConsultation.value.id).length;
});

const isCurrentLocaleSupported = computed(() => getConsultation?.value?.languageEnabled?.includes(currentLocale()));

const isLanguageNameShouldBeLowerCase = computed(() => currentLocale() === 'fr');

function openDemographicPopup(origin) {
  if (community.value.options.use_demographics) {
    track({
      event: 'demographic_dialog_open',
      params: {
        consultation_id: getConsultation.value.id,
        triggered_by: origin,
      },
    });
    demographicDialogRef.value.openDialog();
  }
}

function openHomepageItemPrivateNoAccessPopup() {
  homepageItemPrivateNoAccessDialogRef.value.openDialog();
}

function openAttachmentDialog(type: number, form_id?: number) {
  switch (type) {
    case constant.Context.Parent.consultation:
      track({
        event: 'attachment_dialog_open',
        params: {
          context: 'consultation',
          consultation_id: getConsultation.value.id,
        },
      });
      break;
    case constant.Context.Parent.section:
      track({
        event: 'attachment_dialog_open',
        params: {
          context: 'section',
          consultation_id: getConsultation.value.id,
          section_id: activeSection.value.id,
        },
      });
      break;
    case constant.Context.Parent.form:
      track({
        event: 'attachment_dialog_open',
        params: {
          context: 'form',
          consultation_id: getConsultation.value.id,
          section_id: activeSection.value.id,
          form_id: form_id,
        },
      });
      break;
  }
}

function submitDemographicData() {
  track({
    event: 'demographic_dialog_submit',
    params: {
      consultation_id: getConsultation.value.id,
    },
  });
}

function filterForms(form) {
  const step = form.answers?.steps?.[0];
  if (!step) return false;

  switch (form.type) {
    case constant.Form.Type.Proposal:
    case constant.Form.Type.Question:
      return step.choices?.length > 0;

    case constant.Form.Type.Level:
      return step.level_choices?.length > 0 && step.levels?.length > 0;

    default:
      return true;
  }
}

function isFinalQuestion(formIndex: number) {
  return (
    activeSectionIndex.value + 1 === getConsultation.value.section.length &&
    formIndex + 1 === activeSection.value.form.length
  );
}
</script>

<template>
  <banner
    v-if="props.isTemplate"
    :message="getLabel('consultation.template.banner', communityStore.community?.languageEnabled)"
  />
  <banner
    v-if="!isCurrentLocaleSupported"
    :message="
      getLabel('consultation.not-available-in-current-locale', communityStore.community?.languageEnabled, {
        named: { language: getLanguageName(getConsultation?.languageEnabled, isLanguageNameShouldBeLowerCase) },
      })
    "
  />
  <div class="consultation">
    <q-layout>
      <q-page-container>
        <q-page v-if="getConsultation && userIsAllowed.allowed">
          <div class="consultation__banner">
            <img
              :src="getBanner?.url"
              :alt="getAltText(getBanner, 'alt-text.consultation.banner', getConsultation?.languageEnabled)"
            />
          </div>
          <div class="consultation__container">
            <div class="consultation__header">
              <BaseCard>
                <template v-slot:content>
                  <HomepageItemCard
                    :start-date="getConsultation.beginDate"
                    :end-date="getConsultation.endDate"
                    :title="getTranslation(getConsultation, 'title', getConsultation?.languageEnabled)"
                    :key-information="getConsultation.summary"
                    id="consultation-container"
                    :item-type="constant.HomePageItem.Type.Consultation"
                    :language-enabled="getConsultation?.languageEnabled"
                  />
                  <ContextMenu
                    :sections="getSections(getConsultation.id)"
                    :context="getTranslation(getConsultation, 'description', getConsultation?.languageEnabled)"
                    :context-attachment="getConsultation.context"
                    :active-section="activeSection"
                    :attachments-opened="getConsultation.attachmentsOpened"
                    :video="
                      getConsultation.welcomeVideo
                        ? getTranslation(getConsultation, 'welcomeVideo', getConsultation?.languageEnabled)
                        : ''
                    "
                    :mailing-list="getConsultation.options.mailingList"
                    @changeSection="setActiveTheme($event)"
                    @open-attachment-dialog="openAttachmentDialog(constant.Context.Parent.consultation)"
                    :consultation-id="getConsultation.id"
                    :language-enabled="getConsultation?.languageEnabled"
                  />
                </template>
              </BaseCard>
            </div>
            <div class="section__container">
              <div
                v-if="sectionLoaded && activeSection && getSections(getConsultation.id).length > 1"
                class="sections-heading"
              >
                <h2>
                  {{
                    getLabel('consultation.sections', getConsultation?.languageEnabled, {
                      plural: getSections(getConsultation.id).length,
                    })
                  }}
                </h2>
              </div>

              <div
                v-if="sectionLoaded && activeSection && getSections(getConsultation.id).length > 1"
                class="section__header"
                id="section-container"
              >
                <SectionContainer
                  :sections="getSections(getConsultation.id)"
                  :active-section="activeSection"
                  :consultation-banner="getBanner"
                  :context="getTranslation(activeSection, 'description', getConsultation?.languageEnabled)"
                  :context-attachment="activeSection.context"
                  :context-opened="activeSection.contextOpened"
                  :attachments-opened="activeSection.attachmentsOpened"
                  @changeSection="setNextActiveTheme($event)"
                  @open-attachment-dialog="openAttachmentDialog(constant.Context.Parent.section)"
                  :language-enabled="getConsultation?.languageEnabled"
                />
              </div>
              <div
                v-else-if="!sectionLoaded && activeSection && getSections(getConsultation.id).length > 1"
                class="section__container-section-skeleton"
              >
                <q-skeleton animation="none" width="100%" height="280px" />
              </div>
              <div v-if="formsLoaded && activeSection?.form.length >= 1" class="question-container">
                <div class="questions-heading">
                  <h2 class="questions" v-if="!(showQuestionResults || showAdminResults)">
                    {{ getLabel('consultation.questions', getConsultation?.languageEnabled) }}
                  </h2>
                  <h2 class="questions" v-else>
                    {{ getLabel('consultation.results', getConsultation?.languageEnabled) }}
                  </h2>
                </div>
                <div v-for="(fiche, index) in activeSection.form.filter(filterForms)" :key="index">
                  <div class="consultation__proposals--card">
                    <FormCard
                      :key="fiche.id"
                      :consultation-id="getConsultation.id"
                      :consultation-slug="getConsultation.slug"
                      :fiche="fiche"
                      :index="index"
                      :active-section-index="activeSectionIndex"
                      :section-count="getSections(getConsultation.id).length"
                      :form-count="activeSection.form.length"
                      :show-question-results="showQuestionResults"
                      :anonymousComments="getConsultation.options.anonymous_comments"
                      :display-demographic-data="community.options.use_demographics"
                      :language-enabled="getConsultation?.languageEnabled"
                      :section-prohibits-results-and-comments="sectionProhibitsResultsAndComments"
                      :show-admin-results="showAdminResults"
                      @click="scrollNextQuestion(index)"
                      @openDemographicDialog="openDemographicPopup"
                      @open-attachment-dialog="openAttachmentDialog(constant.Context.Parent.form, $event)"
                    />
                  </div>
                  <div v-if="getConsultation.conclusionMessage.enabled && isFinalQuestion(index)">
                    <ConclusionMessage
                      :conclusionMessage="getConsultation.conclusionMessage"
                      :consultation-has-project="!!project"
                      :project-slug="project?.slug"
                      :language-enabled="getConsultation?.languageEnabled"
                    />
                  </div>
                </div>
              </div>

              <div class="actions" v-if="formsLoaded && !isLastSection">
                <BaseButton
                  class="action"
                  icon="keyboard_arrow_right"
                  :label="getLabel('buttons.next-section', getConsultation?.languageEnabled)"
                  @click="nextSection(activeSection.orderNum + 1)"
                />
              </div>
            </div>
          </div>
          <q-page-sticky
            position="bottom-right"
            :offset="[18, 18]"
            v-if="activeSection && activeSection.citizenProposalEnabled && !(showQuestionResults || showAdminResults)"
          >
            <q-btn fab color="secondary" @click="openDialog">
              <q-icon name="add" size="26px" />
              <span class="q-mx-sm">{{
                getLabel('dialog.proposition.ajouter', getConsultation?.languageEnabled)
              }}</span>
            </q-btn>
          </q-page-sticky>
        </q-page>
      </q-page-container>
    </q-layout>
    <DemographicDialog
      ref="demographicDialogRef"
      @submit="submitDemographicData"
      :language-enabled="getConsultation?.languageEnabled"
    />
    <PropositionDialog
      :section-id="activeSection && activeSection.id"
      :language-enabled="getConsultation?.languageEnabled"
      ref="propositionDialogRef"
    />
    <HomepageItemPrivateNoAccessDialog
      ref="homepageItemPrivateNoAccessDialogRef"
      :home-link="userStore.isUserLogged"
      :need-login="!userStore.isUserLogged"
      :item="getConsultation"
      :language-enabled="getConsultation?.languageEnabled"
    />
  </div>
</template>

<style lang="scss">
.container {
  display: flex;
  overflow-x: auto;
}

.container img {
  margin-right: 15px;
  width: 225px;
}

.container::-webkit-scrollbar {
  display: none;
}

.consultation__container {
  display: flex;
}

.section__container {
  display: flex;
  width: 60%;
  flex-direction: column;
  justify-content: flex-start;

  .section__submenu {
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin: 0px 10% 0px 10%;
  }

  .form-container {
    display: flex;
    flex-direction: column;
    gap: $space-md;
  }

  &-section-skeleton {
    margin: 0 10% $space-md 10%;

    .q-skeleton {
      border-radius: 8px;
    }
  }

  .actions {
    margin: $space-md 10%;
    display: flex;
    justify-content: center;

    .q-btn {
      font-size: 16px;
      font-weight: 500;
    }
  }

  .question-container {
    display: flex;
    flex-direction: column;
    gap: $space-md;
  }

  .admin__results {
    h2 {
      margin: $space-md 10% !important;
    }
  }
}

.isSection {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.consultation {
  &__banner {
    max-height: 360px;

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__header {
    width: 40%;
    position: sticky !important;
    z-index: 1;
    height: 100%;
    top: 120px;

    .homepage-card {
      height: 100%;
    }
  }

  .consultation-card {
    cursor: default;

    &:hover {
      box-shadow: none;
    }

    &__title {
      display: flex;
    }

    &__completion {
      font-weight: bold;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      font-size: 10px;
      margin-bottom: $space-sm;

      .q-linear-progress {
        color: $color-primary;
      }
    }

    &__key-info {
      color: $color-neutral-grey-400-50;
    }
  }

  &__container {
    max-width: 1100px;
    margin: $space-xl auto;
    display: flex;
    flex-direction: row;
  }

  &__themes {
    &--title {
      display: flex;
      align-items: center;
      font-size: 16px;
      font-weight: bold;
      padding: 16px;

      .q-icon {
        margin-right: $space-sm;
        font-size: 28px;
      }
    }

    &--cards {
      display: flex;
      justify-content: space-around;
      margin-bottom: $space-xl;
    }

    &--card {
      border-radius: 8px;
      padding: $space-sm;
      background-color: $color-neutral-white;
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      &.active {
        outline: 2px solid $color-primary;
      }

      &:hover {
        outline: 2px solid $color-primary;
        transition: all 200ms ease;
      }
    }
  }

  &__proposals {
    &--card {
      margin: 0 10%;
      border-radius: $space-sm;
      background-color: $color-neutral-white;
      display: flex;
      align-items: center;
      justify-content: center;

      &.completed {
        background-color: $color-red-12;
      }
    }
  }

  .sections-heading {
    margin: 0 10% $space-md 10%;
  }

  .questions-heading {
    margin: $space-md 10%;

    .questions {
      margin-top: $space-md !important;
    }
  }

  .q-page-sticky {
    z-index: 3;
  }
}

@media screen and (max-width: $breakpoint-xl) {
  .consultation {
    &__container {
      margin-top: $space-md;
    }

    &__themes {
      &--title {
        padding: $space-md;
      }
    }
  }
}

@media screen and (max-width: $breakpoint-lg) {
  .consultation {
    &__themes {
      &--cards {
        width: v-bind(containerWidth);
        overflow-x: auto;
        justify-content: initial;
        gap: $space-sm;
      }

      &--cards::-webkit-scrollbar {
        display: none;
      }
    }
  }
}

@media screen and (max-width: $breakpoint-md) {
  .consultation {
    &__themes {
      &--title {
        position: sticky;
        top: v-bind(stickyHeight);
        z-index: 1;
      }

      &--title.is-sticky {
        border-bottom: 1px solid $color-neutral-grey-300;
        background: $color-neutral-white;
      }
    }
  }
}

@media screen and (max-width: $breakpoint-sm) {
  .consultation {
    &__container {
      flex-direction: column;
    }

    &__header {
      margin: 0 $space-md $space-sm $space-md;
      width: auto;
      top: 0px;
      position: relative !important;
    }

    &__proposals--card {
      margin: $space-md 0;
    }
  }

  .section__container {
    width: auto;
    margin: 0 $space-md $space-sm $space-md;

    .section__submenu {
      margin: $space-sm $space-md;
    }

    .form-container {
      display: flex;
      flex-direction: column;
      gap: 0px;
    }

    .section__header {
      margin: $space-sm 0;
      width: 100%;
    }

    .sections-heading {
      margin-top: $space-md;
    }

    .question-container {
      display: flex;
      flex-direction: column;
      gap: 0;
    }
  }
}
</style>
